import {
  GridColumn as Column,
  GridCellProps,
} from '@progress/kendo-react-grid';
import React, { HTMLProps, useState } from 'react';
import NewReferralButton from 'v3/Components/Shared/NewReferralButton';
import {
  DateCell,
  EditDeleteCell,
  EnumerationCell,
  FilterBarMainGrid,
  LinkCell,
} from '../../Components/Grid';
import {
  FormProvider,
  GridProvider,
  useEnums,
  useGlobalContext,
  useGridState,
} from '../../Context';
import {
  defaultGridProps,
  FilterBarFilterType,
  FilterConfig,
  IFilterConfig,
} from '../../Models';
import { Permission } from '../../Models/Templates/Permission/Permission';
import { ENDPOINTS } from '../../Models/Templates/Routes/Routes';
import {
  addEditRoute,
  AddEditSubRoute,
  ClaimsSubRoute,
  ReferralSubRoute,
  ROUTES,
} from '../../Navigation';
import { ReferralsRequestSubRoute } from '../../Navigation/SubRoutes/ReferralsRequestSubRoutes';
import {
  buildEndpoint,
  buildRoute,
  DEFAULT_DATETIME24H_FORMAT,
  useGridSearch,
  useHasPermissions,
} from '../../Utilities';
import { DebouncedTextFilterCell } from 'Components/Grid/FilterCells/DebouncedTextFilterCell';
import { DatePickerFilterCell } from 'Components/Grid/FilterCells/DatePickerFilterCell';
import { useHistory } from 'react-router-dom';
import { dueDateTimeCell } from 'Components/Grid/DisplayCells/CustomCells';
import { ColumnMenu } from 'Components/Grid/TableHeader/ColumnMenu';
import { DateRange } from '@mui/icons-material';
import { DateRangePickerFilterCell } from 'Components/Grid/FilterCells/DateRangePickerFilterCell';

class ReferralsRequestSearch {
  searchKey?: string;
  id?: string;
}

export enum ReferralsRequestGridIds {
  DetailedSearch = 'detailedSearch',
}

export const ReferralsRequests: React.FC = () => {
  return (
    <GridProvider entityName="Referral Request">
      <div className="main-grid-header">
        <h1>Intake Queue</h1>
        <NewReferralButton />
      </div>
      <ReferralsRequestsFilteredGrid />
    </GridProvider>
  );
};

export type ReferralsRequestsFilteredGridProps =
  HTMLProps<ReferralsRequestsFilteredGridProps> & {
    id?: string;
    enableGridRefresh?: boolean;
  };

export const ReferralsRequestsFilteredGrid: React.FC<
  ReferralsRequestsFilteredGridProps
> = ({ id, ...props }) => {
  const referralSearch = new ReferralsRequestSearch();

  referralSearch.id = id;

  return (
    <FormProvider formModel={referralSearch}>
      <ReferralsRequestsGrid id={id} enableGridRefresh={true} />
    </FormProvider>
  );
};

const ReferralsRequestsGrid: React.FC<ReferralsRequestsFilteredGridProps> = ({
  id,
  ...props
}) => {
  const [gridToggler] = useGridSearch();
  const [filtersEnabled, setFiltersEnabled] = useState(true);
  const dateColumnWidth = filtersEnabled ? '260px' : '200px';
  const history = useHistory();
  const globalContext = useGlobalContext();
  const userdata = globalContext[0]?.currentUser;
  const isInternalUser = userdata?.isInternal || userdata?.isAdmin;

  function NavigateToReferral(dataItem: any, isInternalUser?: boolean) {
    if (dataItem.referralId) {
      history.push(
        buildRoute(AddEditSubRoute.Referral, dataItem.referralId, ROUTES.VIEW)
      );
    } else {
      if (isInternalUser) {
        history.push(
          buildRoute(
            ROUTES.ADD_EDIT,
            AddEditSubRoute.ReferralsRequest,
            dataItem.id +
              '?lob=' +
              dataItem.lineOfBusiness_Value +
              '&draft=0&clientId=' +
              dataItem.clientId +
              (dataItem.referralSourceId
                ? '&referrerId=' + dataItem.referralSourceId
                : '')
          )
        );
      } else {
        history.push(
          buildRoute(AddEditSubRoute.ReferralsRequest, dataItem.id, ROUTES.VIEW)
        );
      }
    }
  }

  function navigateToDraft(
    id: string,
    lob: string,
    clientId?: string,
    referrerId?: string
  ) {
    history.push(
      buildRoute(
        ROUTES.ADD_EDIT,
        AddEditSubRoute.ReferralsRequest,
        id +
          '?lob=' +
          lob +
          '&draft=0&clientId=' +
          clientId +
          (referrerId ? '&referrerId=' + referrerId : '')
      )
    );
  }

  function onRowClick(dataItem: any) {
    navigateToDraft(
      dataItem?.id,
      dataItem?.lineOfBusiness_Value,
      dataItem?.clientId,
      dataItem?.referralSourceId
    );
  }

  let endpoint = ENDPOINTS.referralsRequests.read;
  return (
    <React.Fragment>
      <FilterBarMainGrid
        gridId="IntakeReferrals"
        style={{ cursor: 'pointer' }}
        onRowClick={(e) => NavigateToReferral(e.dataItem, isInternalUser)}
        isMainGrid
        headerTitle="Referrals"
        refreshToggle={gridToggler}
        readEndpoint={endpoint}
        hideExportIcon={true}
        filterBarProps={{
          filterConfigurations: [],
          filterValues: {},
          isColumnSelectorEnabled: false,
        }}
        apiVersion="3.0"
        enableGridRefresh={true}
        defaultFiltersEnabled={true}
        onFiltersEnabledChange={setFiltersEnabled}
        {...defaultGridProps}
      >
        <Column
          field="stradixNumber"
          cell={ReferralNameCell}
          title="Stradix Number"
          locked={true}
          width="150px"
          filterable
          filterCell={DebouncedTextFilterCell}
          columnMenu={ColumnMenu}
        />
        <Column
          field="name"
          cell={ReferralNameCell}
          title="Claimant Name"
          filterable
          filterCell={DebouncedTextFilterCell}
          width="200px"
          columnMenu={ColumnMenu}
        />
        <Column
          field="clientName"
          title="Client"
          filterable
          filterCell={DebouncedTextFilterCell}
          width="200px"
          columnMenu={ColumnMenu}
        />
        <Column
          field="status"
          title="Status"
          cell={ConvertCell}
          filterable={false}
          sortable={true}
          width="200px"
          columnMenu={ColumnMenu}
        />
        <Column
          field="claimNumber"
          title="Claim Number"
          cell={claimLinkCell}
          filterable
          filterCell={DebouncedTextFilterCell}
          width="200px"
          columnMenu={ColumnMenu}
        />
        <Column
          field="referralDate"
          title="Referral Date"
          cell={dotNetDateColumn}
          width={dateColumnWidth}
          filterable
          filterCell={(e) => <DateRangePickerFilterCell {...e} />}
          columnMenu={ColumnMenu}
        />
        <Column
          field="dueDate"
          title="Due Date"
          cell={dueDateTimeCell}
          width={dateColumnWidth}
          filterable
          filterCell={(e) => <DateRangePickerFilterCell {...e} />}
          columnMenu={ColumnMenu}
        />
        <Column
          field="referralRequestType"
          title="Request Type"
          filterable
          filterCell={DebouncedTextFilterCell}
          width="200px"
          columnMenu={ColumnMenu}
        />
        <Column
          field="evalType"
          title="Type of Eval"
          filterable
          filterCell={DebouncedTextFilterCell}
          width="200px"
          columnMenu={ColumnMenu}
        />
        <Column
          field="ownerName"
          title="Owner"
          width="160px"
          columnMenu={ColumnMenu}
          filterCell={DebouncedTextFilterCell}
        />
        <Column
          field="id"
          width="50px"
          title={' '}
          filterable={false}
          sortable={false}
          cell={ActionsCellEdit}
        />
      </FilterBarMainGrid>
    </React.Fragment>
  );
};

const claimLinkCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  return props.dataItem.claimId ? (
    <LinkCell
      {...props}
      linkField={props.dataItem.claimantId}
      endpoint={buildRoute(
        ROUTES.CLAIM,
        props.dataItem.claimId,
        ClaimsSubRoute.View
      )}
    />
  ) : (
    <td>{props.dataItem.claimNumber}</td>
  );
};

const dotNetDateColumn: React.FC<GridCellProps> = (props: GridCellProps) => {
  return <DateCell {...props} dateFormat={DEFAULT_DATETIME24H_FORMAT} />;
};

const ReferralNameCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  const globalContext = useGlobalContext();
  const userdata = globalContext[0]?.currentUser;
  const isInternalUser = userdata?.isInternal || userdata?.isAdmin;
  if (props.dataItem.referralId) {
    return (
      <LinkCell
        endpoint={ROUTES.REFERRAL}
        linkField="referralId"
        subRoute={ReferralSubRoute.View}
        {...props}
      ></LinkCell>
    );
  }

  if (isInternalUser) {
    return (
      <LinkCell
        endpoint={buildRoute(
          ROUTES.ADD_EDIT,
          AddEditSubRoute.ReferralsRequest,
          props.dataItem.id +
            '?lob=' +
            props.dataItem.lineOfBusiness_Value +
            '&draft=0&clientId=' +
            props.dataItem.clientId +
            (props.dataItem.referralSourceId
              ? '&referrerId=' + props.dataItem.referralSourceId
              : '')
        )}
        linkField="id"
        {...props}
      ></LinkCell>
    );
  }
  return (
    <LinkCell
      endpoint={ROUTES.REFERRALSREQUEST}
      linkField="id"
      subRoute={ReferralsRequestSubRoute.View}
      {...props}
    ></LinkCell>
  );
};

const ConvertCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  const hasPermission = useHasPermissions();
  const status = props.dataItem?.status;
  const convertText = (text: string) => 'Convert to Claimant';

  let content;

  if (
    status === 'Pending' &&
    hasPermission([Permission.ConvertReferralRequest])
  ) {
    content =
      props.dataItem?.referralRequestType?.displayName === 'Commercial' ? (
        <td>{status}</td>
      ) : (
        <LinkCell
          {...props}
          endpoint={buildRoute(ROUTES.REFERRALREQUESTPROPOSALS)}
          linkField="id"
          field="status"
          convertFunction={convertText}
        ></LinkCell>
      );
  } else {
    content = <td>{status}</td>;
  }

  return content;
};

const ActionsCellEdit: React.FC<GridCellProps> = (props: GridCellProps) => {
  return (
    <EditDeleteCell
      deleteEndpoint={ENDPOINTS.referralsRequests.base}
      deletePermissions={[Permission.DeleteReferralsRequest]}
      {...props}
    ></EditDeleteCell>
  );
};
